/**
 * ConfigState
 * Validates the consumer's init config and holds the immutable values derived from it.
 */

import {
  AndDoneJSEnvironment,
  AJS_FRAGMENT_URLS,
  AJS_HUB_URLS,
  FRAGMENT_FORM_INPUT_OPTION_TYPES,
  FragmentMountConfig,
  InitConfig,
  FragmentURL,
  HubURL,
  isFragmentFormInputOptionKey,
  isValidFragmentFormInputOptionValue,
  normalizeOrigin,
} from '@anddone/ajs-util';
import type { FragmentFormInputOptions, FragmentName } from '@anddone/ajs-util';
import { createLogger } from '@anddone/ajs-logger';

declare const __ANDDONE_LOCAL_DEV__: boolean | undefined;

const logger = createLogger('SDK', 'ConfigState');

export class ConfigState {
  // TC39 private fields exposed through read-only getters so neither the
  // consuming application nor console access can alter them after init.
  readonly #paymentIntentId: string;
  readonly #appKey: string;
  readonly #environment: AndDoneJSEnvironment;
  readonly #fragmentsToLoad: readonly FragmentMountConfig[];
  readonly #fragmentURL: FragmentURL;
  readonly #hubURL: HubURL;
  // Trust boundary for both inbound and outbound postMessage traffic.
  readonly #allowedFragmentOrigin: string;

  public get paymentIntentId(): string {
    return this.#paymentIntentId;
  }
  public get appKey(): string {
    return this.#appKey;
  }
  public get environment(): AndDoneJSEnvironment {
    return this.#environment;
  }
  public get fragmentsToLoad(): readonly FragmentMountConfig[] {
    return this.#fragmentsToLoad;
  }
  public get fragmentURL(): FragmentURL {
    return this.#fragmentURL;
  }
  public get hubURL(): HubURL {
    return this.#hubURL;
  }
  public get allowedFragmentOrigin(): string {
    return this.#allowedFragmentOrigin;
  }

  constructor(configFromConsumer: InitConfig) {
    // Validate and set environment
    if (!configFromConsumer.environment) {
      logger.error('init', `Config added without environment`, {
        hasPaymentIntentId: Boolean(configFromConsumer.paymentIntentId),
        hasAppKey: Boolean(configFromConsumer.appKey),
        fragmentCount: configFromConsumer.fragments?.length ?? 0,
      });

      throw new Error('environment is required');
    }

    this.#environment = this.setupEnvironment(configFromConsumer.environment);
    logger.info('init', `Config environment declared`, this.#environment);

    // Create shared error report for debug logs
    const consumerConfigErrorReport = {
      hasAppKey: Boolean(configFromConsumer.appKey),
      fragmentCount: configFromConsumer.fragments?.length ?? 0,
      consumerEnvironment: configFromConsumer.environment,
      environment: this.#environment,
    };

    // Validate and set paymentIntentId
    if (!configFromConsumer.paymentIntentId) {
      logger.error('init', `Config added without paymentIntentId`, consumerConfigErrorReport);
      throw new Error('paymentIntentId is required');
    }

    this.#paymentIntentId = configFromConsumer.paymentIntentId;

    // Validate and set appKey
    if (!configFromConsumer.appKey) {
      logger.error('init', `Config added without appKey`, consumerConfigErrorReport);
      throw new Error('appKey is required');
    }

    this.#appKey = configFromConsumer.appKey;

    // Validate and set fragments
    if (!configFromConsumer.fragments?.length) {
      logger.error('init', `Config added without fragments`, consumerConfigErrorReport);
      throw new Error('fragments is required');
    }

    this.#fragmentsToLoad = Object.freeze(
      configFromConsumer.fragments.map(fragmentConfig =>
        this.normalizeFragmentMountConfig(fragmentConfig),
      ),
    );

    this.#fragmentURL = AJS_FRAGMENT_URLS[this.#environment];
    this.#hubURL = AJS_HUB_URLS[this.#environment];
    this.#allowedFragmentOrigin = normalizeOrigin(this.#fragmentURL);
  }

  /**
   * Validates fragment mount options and returns a normalized mount config.
   *
   * @param fragmentConfig - Fragment mount configuration from consumer init config
   * @returns FragmentMountConfig
   */
  private normalizeFragmentMountConfig(fragmentConfig: FragmentMountConfig): FragmentMountConfig {
    if (!fragmentConfig.options) {
      return Object.freeze({ ...fragmentConfig });
    }

    const options = this.validateFragmentFormInputOptions(
      fragmentConfig.fragmentName,
      fragmentConfig.options as Record<string, unknown>,
    );

    if (Object.keys(options).length === 0) {
      return Object.freeze({
        elementId: fragmentConfig.elementId,
        fragmentName: fragmentConfig.fragmentName,
      });
    }

    return Object.freeze({
      ...fragmentConfig,
      options: Object.freeze(options),
    });
  }

  /**
   * Resolves the SDK environment.
   *
   * Consumers may only configure hosted environments.
   * Internal SDK/local development may force local URLs using ANDDONE_LOCAL_DEV.
   *
   * @param environment - Environment value passed in from consumer config.
   * @returns AndDoneJSEnvironment
   */
  private setupEnvironment(environment: InitConfig['environment']): AndDoneJSEnvironment {
    if (__ANDDONE_LOCAL_DEV__) {
      logger.info('init', `Local development mode enabled`);
      return 'local';
    }

    return environment;
  }

  /**
   * Validates and normalises the raw `options` object supplied for a single
   * fragment in the consumer's init config.
   *
   *
   * @param fragmentName - Name of the fragment whose options are being validated
   * @param rawOptions - The raw options object from the consumer init config
   * @returns A `FragmentFormInputOptions` object containing only valid key/value pairs
   */
  private validateFragmentFormInputOptions(
    fragmentName: FragmentName,
    rawOptions?: Record<string, unknown>,
  ): FragmentFormInputOptions {
    if (!rawOptions) {
      return {};
    }

    const options: FragmentFormInputOptions = {};

    for (const [key, value] of Object.entries(rawOptions)) {
      if (!isFragmentFormInputOptionKey(key)) {
        logger.warn('init', `Unknown fragment option "${key}" for "${fragmentName}".`, {
          fragmentName,
          key,
          value,
        });
        continue;
      }

      if (!isValidFragmentFormInputOptionValue(key, value)) {
        logger.warn('init', `Invalid fragment option value for "${key}" on "${fragmentName}".`, {
          fragmentName,
          key,
          value,
          expectedType: FRAGMENT_FORM_INPUT_OPTION_TYPES[key],
        });
        continue;
      }

      options[key] = value;
    }

    return options;
  }
}

Object.freeze(ConfigState.prototype);
